import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Box from '@mui/material/Box';
import { API } from 'aws-amplify';
import orderBy from 'lodash/orderBy';
import React from 'react';
import { CarrierHeaderLink } from '../../types';
import { captureError } from '../../utils/capture-error';
import { navData, NavDataLink, NavDataSection } from './nav-data';
import { NavSectionGroup, NavSectionTab } from './nav-section';

/**
 * The nav sections, with the carriers from /carriers/header added to the
 * Carriers section once they load (the static sections show immediately).
 */
export function useNavData() {
  // State
  const [data, setData] = React.useState<NavDataSection[]>(navData);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response: {
          data: CarrierHeaderLink[];
        } = await API.post('DashboardAPI', '/carriers/header', { body: {} });

        // Filter out children of parent carriers
        const dataFiltered = response.data
          .filter((i) => !i.CarrierCode.startsWith('ap-'))
          .filter((i) => !i.CarrierCode.startsWith('ma-'))
          .filter((i) => !i.CarrierCode.startsWith('ms-'));

        // Order data by Title
        const dataOrdered = orderBy(dataFiltered, 'Title');

        // Format data to match NavDataLink
        const newLinks: NavDataLink[] = [];
        dataOrdered.forEach((i) => {
          newLinks.push({
            title: i.Title,
            to: `/carriers/${i.CarrierCode}`,
            startIcon: <ArrowRightAltIcon fontSize="small" />,
          });
        });

        // Update navigation data
        setData(
          navData.map((section) => {
            if (section.title === 'Carriers') {
              return {
                ...section,
                links: [...section.links, ...newLinks],
              };
            } else {
              return section;
            }
          }),
        );
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    fetchData();
  }, []);

  return data;
}

/** Desktop top nav in the App Bar: one dropdown tab per section */
export function NavTopBar(props: { data: NavDataSection[] }) {
  return (
    <Box
      component="nav"
      aria-label="ARC"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        alignSelf: 'stretch',
        ml: '28px',
        gap: '2px',
        minWidth: 0,
        // If the links ever outgrow the bar, clip them rather than slide
        // under the user control
        overflow: 'hidden',
      }}
    >
      {props.data.map((item) => (
        <NavSectionTab key={item.title} item={item} />
      ))}
    </Box>
  );
}

/** The same sections in the drawer below md, as collapsible groups */
export function NavDrawerBody(props: { data: NavDataSection[] }) {
  return (
    <Box
      component="nav"
      aria-label="ARC"
      sx={{
        p: 1,
        // Keep the last links above the CookieYes consent button that is
        // fixed to the bottom-left corner
        pb: '72px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        overflowY: 'auto',
        minHeight: 0,
        flex: 1,
      }}
    >
      {props.data.map((item) => (
        <NavSectionGroup key={item.title} item={item} />
      ))}
    </Box>
  );
}
